import { FC, ReactNode, useEffect } from 'react'

import { useForm } from '@tanstack/react-form'
import { useQuery } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { handleFormSubmissionError } from 'ui/components'
import { emailHistoryReportQueryOptions } from '~bank-reports/service/emailHistoryReportQueryOptions'
import {
  EmailHistoryReportFilterParams,
  emailHistoryReportFilterParamsSchema
} from '~bank-reports/types/EmailHistoryReport'

import { EmailHistoryReportContext } from './EmailHistoryReportContext'

type EmailHistoryReportProviderProps = {
  children: ReactNode
}

export const EmailHistoryReportContextProvider: FC<EmailHistoryReportProviderProps> = ({
  children
}: EmailHistoryReportProviderProps) => {
  const routeApi = getRouteApi('/_app/reports/email-history')

  const search = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  const formApi = useForm({
    defaultValues: search as Pick<EmailHistoryReportFilterParams, 'dateStart' | 'dateEnd'>
  })

  const emailHistoryReportQuery = useQuery(emailHistoryReportQueryOptions(search))

  useEffect(() => {
    if (emailHistoryReportQuery.error) {
      handleFormSubmissionError<Pick<EmailHistoryReportFilterParams, 'dateStart' | 'dateEnd'>>(
        emailHistoryReportQuery.error,
        formApi
      )
    }
  }, [!!emailHistoryReportQuery.error])

  const value = {
    data: emailHistoryReportQuery.data,
    isPending: emailHistoryReportQuery.isLoading || emailHistoryReportQuery.isRefetching,
    isSuccessFullyLoaded: !!emailHistoryReportQuery.data,
    state: search,
    navigate: (searchParams: Partial<EmailHistoryReportFilterParams>) =>
      navigate({ to: '.', search: (prev) => ({ ...prev, ...searchParams }), resetScroll: false, replace: true }),
    clear: () =>
      navigate({ to: '.', search: emailHistoryReportFilterParamsSchema.parse({}), resetScroll: false, replace: true }),
    formApi
  }

  return <EmailHistoryReportContext.Provider value={value}>{children}</EmailHistoryReportContext.Provider>
}
